// Lecture des résultats du moteur déterministe (GET /scenarios). Aucun calcul de risque n'est refait ici :
// l'application trie, regroupe et explique les montants rendus par le moteur.
import type { Calcul, DetailPerte, Scenario } from '../api/types';
import { decimal, POSTES_PERTE } from './format';

export function trierParPerte(s: Scenario[]): Scenario[] {
  return [...s].sort((a, b) => b.perte.haut.valeur - a.perte.haut.valeur || a.id.localeCompare(b.id));
}

/**
 * Contribution d'un scénario aux pertes potentielles estimées par an (`perte_annuelle_attendue` du moteur) :
 * fréquence estimée par an × perte par sinistre, bas avec bas et haut avec haut.
 */
export function contributionAnnuelle(s: Scenario): [number, number] {
  return [s.frequence_annuelle[0] * s.perte.bas.valeur, s.frequence_annuelle[1] * s.perte.haut.valeur];
}

/**
 * Explication des pertes potentielles estimées par an (`perte_annuelle_attendue` du moteur) : somme des
 * contributions. Rend aussi l'écart avec la valeur du moteur, qui doit être nul (sinon l'application l'affiche
 * plutôt que de le cacher).
 */
export function verifierPerteAnnuelle(c: Calcul): { somme: [number, number]; ecart: [number, number] | null } {
  const somme: [number, number] = [0, 0];
  for (const s of c.scenarios) {
    const [b, h] = contributionAnnuelle(s);
    somme[0] += b;
    somme[1] += h;
  }
  return {
    somme,
    ecart: c.perte_annuelle_attendue
      ? [somme[0] - c.perte_annuelle_attendue.bas.valeur, somme[1] - c.perte_annuelle_attendue.haut.valeur]
      : null,
  };
}

export interface LignePoste {
  cle: string;
  libelle: string;
  bas: number;
  haut: number;
  compte: boolean;
}

/** Les postes des pertes potentielles estimées par sinistre, dans l'ordre, et le total vérifié (somme des postes comptés). */
export function postes(detail: [DetailPerte, DetailPerte]): { lignes: LignePoste[]; total: [number, number] } {
  const lignes = POSTES_PERTE.map((p) => ({
    ...p,
    bas: (detail[0] as unknown as Record<string, { valeur: number }>)[p.cle]?.valeur ?? 0,
    haut: (detail[1] as unknown as Record<string, { valeur: number }>)[p.cle]?.valeur ?? 0,
  }));
  return { lignes, total: [detail[0].total.valeur, detail[1].total.valeur] };
}

export function sommePostesComptes(lignes: LignePoste[]): [number, number] {
  return lignes.filter((l) => l.compte).reduce<[number, number]>((a, l) => [a[0] + l.bas, a[1] + l.haut], [0, 0]);
}

/** Portes d'entrée : composants d'où part un scénario d'attaque, de celui aux pertes potentielles estimées les plus élevées au moins élevées. */
export function portesEntree(c: Calcul): { entree: string; motif: string; scenario: Scenario }[] {
  return trierParPerte(c.scenarios.filter((s) => s.nature === 'attaque')).map((s) => ({
    entree: s.entree,
    motif: s.motif,
    scenario: s,
  }));
}

export function scenarioDePanne(c: Calcul, composant: string): Scenario | undefined {
  return c.scenarios.find((s) => s.nature === 'panne' && s.entree === composant);
}

/**
 * Indicateur de progression NIS2 en un seul libellé, le même partout (T6) : le `score_nis2` du moteur (mesure
 * tenue = 1, partielle = 0,5, en écart = 0 ; les valeurs techniques restent `conforme`, `partiel`, `ecart`) et le
 * décompte des mesures évaluées, par exemple « 0,5 / 10 » et « 0 tenue, 1 partielle, 9 en écart ». Ce n'est pas
 * une certification de conformité (docs/REGLE-EDITORIALE.md).
 */
export function libelleNis2(c: Pick<Calcul, 'nis2' | 'indicateurs'>): { score: string; detail: string; complet: boolean } {
  const total = c.nis2.length;
  const n = (st: string) => c.nis2.filter((m) => m.statut === st).length;
  const conformes = n('conforme');
  const partielles = n('partiel');
  const ecarts = total - conformes - partielles;
  return {
    score: `${decimal(c.indicateurs.score_nis2)} / ${total}`,
    detail: `${conformes} tenue${conformes > 1 ? 's' : ''}, ${partielles} partielle${partielles > 1 ? 's' : ''}, ${ecarts} en écart`,
    complet: total > 0 && conformes === total,
  };
}
